import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../UIProvider';

/**
 * The fetch lifecycle every report shares: load on mount and whenever the filters change,
 * expose { data, loading, reload }, toast on failure. A stale response that lands after a newer
 * request is thrown away, so fast filter-clicking can't paint old numbers over new ones.
 *
 * @param fetcher async () => payload - memoise it (useCallback) on the filter values
 */
const useReport = (fetcher) => {
  const notify = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const payload = await fetcher();
      if (seq.current === mine) setData(payload);
    } catch (e) {
      if (seq.current === mine) notify('error', e.message || 'The report could not be loaded');
    } finally {
      if (seq.current === mine) setLoading(false);
    }
  }, [fetcher, notify]);

  useEffect(() => { reload(); }, [reload]);

  return { data, loading, reload };
};

export default useReport;
